import FocusableOverlaidImage from '@rhg/components/focusable-overlaid-image';
import { AudioPlayer } from '@rhg/components/media/audio-player';
import { MediaTrackerFn } from '@rhg/hooks/use-media-tracker/types';
import commonMessages from '@rhg/lang/messages/common';
import playerMessages from '@rhg/lang/messages/player';
import { Box, Theme, useMediaQuery, useTheme } from '@rhg/libs/material';
import { ChevronUp } from '@rhg/libs/material/icons';
import { responsivePaddingDefault } from '@rhg/libs/material/sx';
import { MediaEvent } from '@rhg/utils/media';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { AudioPlayerControls, AudioPlayerVariant } from '../audio-player/types';
import { AudioPlayerDrawerProps } from './types';

export const AUDIO_PLAYER_DRAWER_HEIGHT = {
    xs: 72,
    sm: 72,
    md: 72,
    lg: 108,
    xl: 108,
};

const IMAGE_SIZE = {
    xs: AUDIO_PLAYER_DRAWER_HEIGHT.xs - 5,
    sm: AUDIO_PLAYER_DRAWER_HEIGHT.sm - 5,
    md: AUDIO_PLAYER_DRAWER_HEIGHT.md - 5,
    lg: AUDIO_PLAYER_DRAWER_HEIGHT.lg,
    xl: AUDIO_PLAYER_DRAWER_HEIGHT.xl,
};

const stylePlayerPlaceholder = {
    width: '100%',
    height: AUDIO_PLAYER_DRAWER_HEIGHT,
};

const stylePlayerDrawer = {
    position: 'fixed',
    bottom: 0,
    left: 0,
    width: '100%',
    height: AUDIO_PLAYER_DRAWER_HEIGHT,
    zIndex: (theme: Theme) => theme.zIndex.appBar,
};

const stylePlayerContainer = {
    position: 'relative',
    width: '100%',
    height: '100%',
    p: responsivePaddingDefault,
};

const styleImage = {
    maxWidth: IMAGE_SIZE,
    maxHeight: IMAGE_SIZE,
    minWidth: IMAGE_SIZE,
    minHeight: IMAGE_SIZE,
};

const styleAudioPlayer = {
    position: 'absolute',
    bottom: 0,
    width: '100%',
    left: 0,
};

const styleAudioControls = {
    borderTop: '1px solid',
    borderColor: 'divider',
    height: AUDIO_PLAYER_DRAWER_HEIGHT,
};

const styleFocusIconWrapper = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: '1px solid white',
    borderRadius: '50%',
    width: { xs: 32, sm: 32, md: 32, lg: 48, xl: 48 },
    height: { xs: 32, sm: 32, md: 32, lg: 48, xl: 48 },
};
const styleFocusIcon = {
    color: 'white',
    width: 24,
};

export function AudioPlayerDrawer({
    audio,
    title,
    drawerTitleIcon,
    subtitle,
    imageSrc,
    customAction,
    isOpen,
    controls = AudioPlayerControls.Regular,
    supportsPlaybackSpeed,
    previousProgress,
    autoplay,
    onMaximize,
    onProgress,
    onEnd,
    onClose,
    onNextTrack,
    onPreviousTrack,
    onControlClick,
}: AudioPlayerDrawerProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const { formatMessage } = useIntl();
    const [hasFocus, setHasFocus] = useState(false);

    const progressTracker = useCallback<MediaTrackerFn>(
        (progress) => {
            onProgress?.(progress);
            if (progress.eventType === MediaEvent.Ended) {
                onEnd?.();
            }
        },
        [onEnd, onProgress]
    );

    // Escape based on escape key
    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => {
            if (e.code === 'Escape') {
                e.preventDefault();
                onClose?.();
            }
        };
        document.addEventListener('keyup', handleKey);
        return () => {
            document.removeEventListener('keyup', handleKey);
        };
    }, [onClose]);

    const imageElement = useMemo(
        () =>
            imageSrc ? (
                <FocusableOverlaidImage
                    src={imageSrc}
                    alt={formatMessage(playerMessages['player.expand'])}
                    ratio="1:1"
                    trackButton="MaximizePlayer"
                    onClick={onMaximize}
                    onMouseEnter={() => setHasFocus(true)}
                    onMouseLeave={() => setHasFocus(false)}
                    onFocus={() => setHasFocus(true)}
                    onBlur={() => setHasFocus(false)}
                    isFocused={hasFocus}
                    iconFocus={
                        <Box sx={styleFocusIconWrapper}>
                            <ChevronUp sx={styleFocusIcon} />
                        </Box>
                    }
                    sx={styleImage}
                />
            ) : null,
        [hasFocus, imageSrc, onMaximize, formatMessage]
    );

    return isOpen ? (
        <>
            <Box sx={stylePlayerPlaceholder} />
            <Box sx={stylePlayerDrawer}>
                <Box
                    sx={stylePlayerContainer}
                    role="contentinfo"
                    aria-label={formatMessage(
                        commonMessages['common.audio_player']
                    )}
                >
                    {!!audio && (
                        <AudioPlayer
                            variant={
                                isMobile
                                    ? AudioPlayerVariant.Minimal
                                    : AudioPlayerVariant.Collapsed
                            }
                            isFloating={true}
                            title={title}
                            drawerTitleIcon={drawerTitleIcon}
                            subtitle={subtitle}
                            loop={controls === AudioPlayerControls.Soundscape}
                            controls={controls}
                            supportsPlaybackSpeed={supportsPlaybackSpeed}
                            image={imageElement}
                            customAction={customAction}
                            autoplay={autoplay}
                            audio={audio}
                            previousProgress={previousProgress}
                            onClose={onClose}
                            trackFn={progressTracker}
                            sx={styleAudioPlayer}
                            controlsSx={styleAudioControls}
                            onNextTrack={onNextTrack}
                            onPreviousTrack={onPreviousTrack}
                            willTrackProgress
                            onControlClick={onControlClick}
                        />
                    )}
                </Box>
            </Box>
        </>
    ) : null;
}
